import { CssBaseline, ThemeProvider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createContext, use, useMemo, type ReactNode } from 'react';
import { api, unwrap, type Schemas } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { createAppTheme } from '../design/theme';
import { DEFAULT_FORMAT, type FormatSettings } from '../lib/format';

export type ShopSettings = Schemas['ShopSettings'];

interface ShopContextValue {
  settings: ShopSettings | null;
  format: FormatSettings;
}

const ShopContext = createContext<ShopContextValue>({ settings: null, format: DEFAULT_FORMAT });

export const settingsQueryKey = ['settings'] as const;

/** Loads the shop's settings after sign-in and themes the app with its brand colour. */
export function ShopProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const { data: settings } = useQuery({
    queryKey: settingsQueryKey,
    queryFn: () => unwrap(api.GET('/settings')),
    enabled: status === 'signedIn',
    staleTime: 5 * 60_000,
  });

  const theme = useMemo(() => createAppTheme(settings?.themeColor), [settings?.themeColor]);
  const value = useMemo<ShopContextValue>(
    () => ({
      settings: settings ?? null,
      format: settings
        ? {
            currencyCode: settings.currencyCode,
            currencyDecimals: settings.currencyDecimals,
            locale: settings.locale,
            timeZone: settings.timeZone,
          }
        : DEFAULT_FORMAT,
    }),
    [settings],
  );

  return (
    // Light by default; the user's choice (toggle) is remembered by MUI in localStorage.
    <ThemeProvider
      theme={theme}
      defaultMode="light"
      modeStorageKey="sm.mode"
      disableTransitionOnChange
    >
      <CssBaseline enableColorScheme />
      <ShopContext value={value}>{children}</ShopContext>
    </ThemeProvider>
  );
}

export function useShop(): ShopContextValue {
  return use(ShopContext);
}
